import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import {
  TESTIMONIALS,
  TESTIMONIALS_DA,
  AVG_RATING,
  REVIEW_COUNT,
} from "@/lib/testimonials";
import { useI18n } from "@/lib/i18n";

/**
 * TestimonialRotator — compact, paper/navy-themed quote rotator.
 *
 * Design choices:
 *  - One quote visible at a time, fixed-height card so the rotation
 *    doesn't shove the rest of the page up and down.
 *  - 7-second auto-advance, paused on hover/focus and on prev/next.
 *  - Dot pips + side arrows (keyboard ←/→ work too) — nothing
 *    auto-plays in a way the reader can't escape.
 *  - Italic display serif for the quote, sans for the attribution
 *    (location is the social-proof, not the name).
 *  - Stars rendered with lucide `Star` filled in navy ink — keeps
 *    the FamFeel cream/navy aesthetic instead of yellow/purple.
 */
export default function TestimonialRotator() {
  const { lang, t: tr } = useI18n();
  // Swap the underlying pool when Danish is active — 4 hand-picked
  // Danish voices instead of the 17-entry international pool. The
  // eyebrow stats line still quotes the global rating/count so we
  // don't fake numbers for the DA locale.
  const pool = lang === "da" ? TESTIMONIALS_DA : TESTIMONIALS;
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = pool.length;

  // Reset the visible index when the pool swaps under us — otherwise
  // switching EN → DA on a page where the user was on quote #12 would
  // land on an out-of-bounds slot with an empty card.
  useEffect(() => {
    setIdx(0);
  }, [lang]);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % total);
    }, 7000);
    return () => clearInterval(t);
  }, [paused, total]);

  // Keyboard arrows when the rotator is focus-visible — small touch,
  // matters for accessibility audits and power-readers.
  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      setIdx((i) => (i - 1 + total) % total);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      setIdx((i) => (i + 1) % total);
    }
  };

  const t = pool[idx];

  return (
    <section
      className="px-6 md:px-10 pt-10 md:pt-12 pb-10 max-w-5xl"
      data-testid="landing-testimonials"
    >
      <div className="label-tiny mb-4 text-[color:var(--ink-3)]">
        {tr({
          en: `${AVG_RATING}/5 · ${REVIEW_COUNT} families across UK, US, Denmark, Sweden, Norway, France, Spain, Italy, Australia & Malta`,
          da: `${AVG_RATING}/5 · ${REVIEW_COUNT} familier fordelt på Storbritannien, USA, Danmark, Sverige, Norge, Frankrig, Spanien, Italien, Australien & Malta`,
        })}
      </div>
      <div
        role="group"
        aria-roledescription="carousel"
        aria-label="Family testimonials"
        tabIndex={0}
        onKeyDown={onKeyDown}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        className="ff-card p-5 md:p-7 relative outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--ink)]/30 rounded-2xl"
      >
        {/* Stars — navy filled, intentionally small so the quote
            doesn't compete with them for attention. */}
        <div
          className="flex items-center gap-0.5 mb-4"
          aria-label={`Rated ${t.rating} out of 5`}
        >
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              className="w-3.5 h-3.5"
              strokeWidth={0}
              fill={i < t.rating ? "var(--ink)" : "var(--line)"}
            />
          ))}
        </div>

        <blockquote
          className="font-display-italic text-xl md:text-2xl leading-snug tracking-tight text-[color:var(--ink)]"
          data-testid={`testimonial-quote-${t.id}`}
          // Keying the inline animation to idx forces a fresh fade-in
          // every slide instead of a hard cut.
          key={t.id}
          style={{ animation: "ff-fade-in .35s ease-out both" }}
        >
          {t.quote}
        </blockquote>

        <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="text-sm text-[color:var(--ink-2)]">
            <span className="font-medium text-[color:var(--ink)]">{t.name}</span>
            <span aria-hidden="true"> · </span>
            <span>{t.location}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIdx((i) => (i - 1 + total) % total)}
              className="w-8 h-8 inline-flex items-center justify-center rounded-full border border-[color:var(--line)] hover:bg-[color:var(--bg-soft)] transition-colors"
              aria-label="Previous testimonial"
              data-testid="testimonial-prev"
            >
              <ChevronLeft className="w-4 h-4" strokeWidth={2} />
            </button>
            <div
              className="flex items-center gap-1.5"
              role="tablist"
              aria-label="Pick testimonial"
            >
              {TESTIMONIALS.map((tv, i) => (
                <button
                  key={tv.id}
                  type="button"
                  role="tab"
                  aria-selected={i === idx}
                  aria-label={`Show testimonial ${i + 1} of ${total}`}
                  onClick={() => setIdx(i)}
                  className={`h-1.5 rounded-full transition-all ${
                    i === idx
                      ? "w-5 bg-[color:var(--ink)]"
                      : "w-1.5 bg-[color:var(--ink)]/20 hover:bg-[color:var(--ink)]/40"
                  }`}
                  data-testid={`testimonial-pip-${i}`}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={() => setIdx((i) => (i + 1) % total)}
              className="w-8 h-8 inline-flex items-center justify-center rounded-full border border-[color:var(--line)] hover:bg-[color:var(--bg-soft)] transition-colors"
              aria-label="Next testimonial"
              data-testid="testimonial-next"
            >
              <ChevronRight className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>
        </div>
      </div>

      {/* "Read every review →" link — hidden on Danish because we
          don't have a Danish full-reviews page yet. Reappears
          automatically the moment /reviews gets a Danish
          translation. */}
      {lang !== "da" && (
        <div className="mt-3 text-right">
          <Link
            to="/reviews"
            className="label-tiny text-[color:var(--ink-2)] hover:text-[color:var(--ink)] inline-flex items-center gap-1"
            data-testid="testimonials-view-all"
          >
            Read every review →
          </Link>
        </div>
      )}
    </section>
  );
}
